import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Modal from "@/components/ui/modal";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tag, FolderPlus, Check, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import ParentCategorySelect from "./ParentCategorySelect";
import MediaLibraryComponent from "../../media/MediaLibraryComponent";

const DEFAULT_FORM_DATA = {
  name: "",
  parent_id: null,
  description: "",
  image: "",
  is_active: true,
  is_featured: false,
  level: 0,
  sort_order: 0,
};

const ProductCategoryModal = ({
  open,
  show,
  onOpenChange,
  onHide,
  categoryToEdit = null,
  initialName = "",
  initialCategoryName = "",
  categories: propCategories = null,
  isLoadingCategories = false,
  onSuccess,
  simpleMode = false,
  onlyBasicFields = false,
  isQuickAdd = false,
  // Backwards compatibility props
  categoryFormData: externalFormData,
  setCategoryFormData: setExternalFormData,
  handleCreateCategory: externalHandleCreate,
  isPending: externalIsPending,
}) => {
  const isSimple = simpleMode || onlyBasicFields || isQuickAdd;
  const queryClient = useQueryClient();
  const isOpen = open ?? show ?? false;

  const handleClose = () => {
    if (onOpenChange) onOpenChange(false);
    else if (onHide) onHide();
  };

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [isParentCategory, setIsParentCategory] = useState(true);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

  // Sync state when modal opens or categoryToEdit changes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (isOpen) {
      const defaultName = initialCategoryName || initialName || "";
      if (categoryToEdit) {
        const hasParent = Boolean(
          categoryToEdit.parent_id ?? categoryToEdit.parent?.id,
        );
        setIsParentCategory(!hasParent);
        setFormData({
          name: categoryToEdit.name || "",
          parent_id: categoryToEdit.parent_id ?? categoryToEdit.parent?.id ?? null,
          description: categoryToEdit.description || "",
          image: categoryToEdit.image || categoryToEdit.media?.[0]?.url || "",
          is_active: categoryToEdit.is_active ?? true,
          is_featured: categoryToEdit.is_featured ?? false,
          level: categoryToEdit.level || 0,
          sort_order: categoryToEdit.sort_order || 0,
        });
      } else if (externalFormData) {
        const hasParent = Boolean(
          externalFormData.parent_id != null && externalFormData.parent_id !== "",
        );
        setIsParentCategory(!hasParent);
        setFormData({
          ...DEFAULT_FORM_DATA,
          name: externalFormData.name || defaultName,
          ...externalFormData,
        });
      } else {
        setIsParentCategory(true);
        setFormData({
          ...DEFAULT_FORM_DATA,
          name: defaultName,
        });
      }
    }
  }, [isOpen, categoryToEdit, externalFormData, initialCategoryName, initialName]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // If categories are not passed as props, fetch them
  const { data: fetchedCategories = [], isLoading: isCategoriesLoading } =
    useQuery({
      queryKey: ["categories-all"],
      queryFn: async () => {
        const res = await apiClient.get(API_ENDPOINTS.INVENTORY.CATEGORIES, {
          params: { perPage: 9999 },
        });
        return res.data?.data || [];
      },
      enabled: isOpen && (!propCategories || propCategories.length === 0),
      staleTime: 30000,
    });

  const isCategoriesLoadingOverall =
    isLoadingCategories ||
    ((!propCategories || propCategories.length === 0) && isCategoriesLoading);

  const availableCategories = useMemo(() => {
    const raw =
      propCategories && propCategories.length > 0
        ? propCategories
        : fetchedCategories;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  }, [propCategories, fetchedCategories]);

  // Media Library handler
  const fetchMediaDetails = async (mediaId) => {
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.MEDIA.MEDIA_DETAILS(mediaId),
      );
      if (res.status === 200 && res.data?.data?.url) {
        setFormData((prev) => ({ ...prev, image: res.data.data.url }));
        if (setExternalFormData) {
          setExternalFormData((prev) => ({
            ...prev,
            image: res.data.data.url,
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch media details", error);
      toast.error("Failed to fetch selected image");
    }
  };

  // Self-contained save mutation
  const saveCategoryMutation = useMutation({
    mutationFn: async (payload) => {
      if (categoryToEdit?.id) {
        const res = await apiClient.put(
          API_ENDPOINTS.INVENTORY.CATEGORY_DETAILS(categoryToEdit.id),
          payload,
        );
        return res.data?.data || res.data;
      } else {
        const res = await apiClient.post(
          API_ENDPOINTS.INVENTORY.CATEGORIES,
          payload,
        );
        return res.data?.data || res.data;
      }
    },
    onSuccess: (savedData) => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["categories-all"] });
      queryClient.invalidateQueries({ queryKey: ["pos-categories-all"] });
      queryClient.invalidateQueries({ queryKey: ["pos-subcategories"] });
      toast.success(
        `Category ${categoryToEdit ? "updated" : "created"} successfully!`,
      );
      handleClose();
      onSuccess?.(savedData);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message ||
        `Failed to ${categoryToEdit ? "update" : "create"} category`,
      );
    },
  });

  const isSaving = externalIsPending ?? saveCategoryMutation.isPending;

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error("Category name is required");
      return;
    }

    if (!isParentCategory && (!formData.parent_id || formData.parent_id === "")) {
      toast.error("Please select a parent category");
      return;
    }

    if (externalHandleCreate) {
      externalHandleCreate();
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim(),
      parent_id:
        isParentCategory || formData.parent_id === "" || formData.parent_id == null
          ? null
          : Number(formData.parent_id),
      level: isParentCategory ? 0 : formData.level || 0,
      sort_order: formData.sort_order || 0,
    };

    saveCategoryMutation.mutate(payload);
  };

  const handleParentCategoryToggle = (checked) => {
    const isParent = Boolean(checked);
    setIsParentCategory(isParent);
    if (isParent) {
      setFormData((prev) => ({
        ...prev,
        parent_id: null,
        level: 0,
      }));
      if (setExternalFormData) {
        setExternalFormData((prev) => ({
          ...prev,
          parent_id: null,
          level: 0,
        }));
      }
    }
  };

  const handleParentSelectChange = (newParentId, selectedCat) => {
    const newLevel = selectedCat ? (selectedCat.level ?? 0) + 1 : 1;
    setFormData((prev) => ({
      ...prev,
      parent_id: newParentId,
      level: newLevel,
    }));
    if (setExternalFormData) {
      setExternalFormData((prev) => ({
        ...prev,
        parent_id: newParentId,
        level: newLevel,
      }));
    }
  };

  const updateField = (field, val) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
    if (setExternalFormData) {
      setExternalFormData((prev) => ({ ...prev, [field]: val }));
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        onOpenChange={(openState) => {
          if (!openState) handleClose();
        }}
        title={categoryToEdit ? "Edit Category" : "Create New Category"}
        icon={categoryToEdit ? Tag : FolderPlus}
        maxWidth={isSimple ? "max-w-md" : "max-w-lg"}
        footer={
          <div className="flex gap-2 justify-end w-full">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? (
                <Spinner size={16} className="mr-2" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              {categoryToEdit ? "Save Changes" : "Create Category"}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Name */}
          <div className="space-y-2">
            <Label htmlFor="category-name">
              Category Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="category-name"
              placeholder="e.g. Computers, Laptops, Accessories"
              value={formData.name}
              onChange={(e) => updateField("name", e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Is Parent Category Checkbox (Frontend Only UI Toggle) */}
          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id="is_parent_category"
              checked={isParentCategory}
              onCheckedChange={handleParentCategoryToggle}
            />
            <Label
              htmlFor="is_parent_category"
              className="cursor-pointer text-sm font-medium leading-none select-none"
            >
              This is a parent category
            </Label>
          </div>

          {/* Hierarchical Parent Category Selection */}
          {!isParentCategory && (
            <div className="space-y-2 pt-1">
              <Label>
                Parent Category <span className="text-destructive">*</span>
              </Label>
              <ParentCategorySelect
                key={categoryToEdit?.id ?? "new"}
                value={formData.parent_id}
                onChange={handleParentSelectChange}
                categories={availableCategories}
                excludeId={categoryToEdit?.id}
                placeholder="Select parent category"
                isLoading={isCategoriesLoadingOverall}
              />
            </div>
          )}

          {!isSimple && (
            <>
              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="category-description">Description</Label>
                <Textarea
                  id="category-description"
                  placeholder="Enter category description (optional)"
                  value={formData.description || ""}
                  onChange={(e) => updateField("description", e.target.value)}
                  rows={2}
                />
              </div>

              {/* Image & Status Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <div className="space-y-2">
                  <Label>Category Image</Label>
                  <div
                    className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:bg-muted/50 transition-colors flex flex-col items-center justify-center min-h-[90px]"
                    onClick={() => setIsMediaLibraryOpen(true)}
                  >
                    {formData.image ? (
                      <div className="relative group">
                        <img
                          src={formData.image}
                          alt="Category"
                          className="h-16 w-16 object-cover rounded-md border"
                        />
                        <div className="text-[11px] text-muted-foreground mt-1 group-hover:underline">
                          Change Image
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted-foreground items-center flex flex-col">
                        <ImageIcon className="h-6 w-6 mb-1 text-muted-foreground/70" />
                        <span className="text-xs">Select from Library</span>
                      </div>
                    )}
                  </div>
                  {formData.image && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive h-auto p-0 text-xs hover:bg-transparent"
                      onClick={() => updateField("image", "")}
                    >
                      Remove Image
                    </Button>
                  )}
                </div>

                <div className="flex flex-col justify-center space-y-3 pl-1">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="category_is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => updateField("is_active", checked)}
                    />
                    <Label htmlFor="category_is_active" className="cursor-pointer text-sm">
                      Active Status
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="category_is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => updateField("is_featured", checked)}
                    />
                    <Label htmlFor="category_is_featured" className="cursor-pointer text-sm">
                      Featured Category
                    </Label>
                  </div>
                </div>
              </div>
            </>
          )}
        </form>
      </Modal>

      {/* Media Library Dialog */}
      {!isSimple && (
        <MediaLibraryComponent
          isOpen={isMediaLibraryOpen}
          onClose={() => setIsMediaLibraryOpen(false)}
          onSelect={(selectedId) => {
            fetchMediaDetails(selectedId);
          }}
          isMultiple={false}
        />
      )}
    </>
  );
};

ProductCategoryModal.propTypes = {
  open: PropTypes.bool,
  show: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onHide: PropTypes.func,
  categoryToEdit: PropTypes.object,
  initialName: PropTypes.string,
  initialCategoryName: PropTypes.string,
  categories: PropTypes.array,
  isLoadingCategories: PropTypes.bool,
  onSuccess: PropTypes.func,
  simpleMode: PropTypes.bool,
  onlyBasicFields: PropTypes.bool,
  isQuickAdd: PropTypes.bool,
  categoryFormData: PropTypes.shape({
    name: PropTypes.string,
    parent_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    image: PropTypes.string,
    is_active: PropTypes.bool,
    is_featured: PropTypes.bool,
  }),
  setCategoryFormData: PropTypes.func,
  handleCreateCategory: PropTypes.func,
  isPending: PropTypes.bool,
};

export default ProductCategoryModal;
